/**
 * LiturgicalNotifications
 * ─────────────────────────────────────────────────────────────────────
 * Subscribes to the centralized CelestialContext (one source of truth
 * for /api/celestial/data) and fires sonner toasts on three
 * biblical-calendar transitions:
 *
 *   1. SUNDOWN   — when sun_altitude crosses below 0° and the biblical
 *                  day rolls over.
 *   2. PREPARATION DAY — Gregorian Friday (before sundown).
 *   3. SABBATH DAY — Friday sundown → Saturday sundown
 *                  (Gregorian weekly cycle — user mandate 2026-02-11).
 *
 * Migrated 2026-02 to consume CelestialContext instead of running its
 * own independent poll — eliminates calendar fragmentation.
 *
 * Switched 2026-02-11 from biblical-day-based Sabbaths
 * (Days 7/14/21/28 of the biblical month) to Gregorian weekly cycle:
 * the backend now exposes `is_sabbath`, `is_preparation`, and
 * `sabbath_mode: "gregorian_weekly"` in the sabbath payload; we trust
 * those flags directly instead of computing client-side.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useCelestial } from "../contexts/CelestialContext";

const LS_KEY = "ylc_liturgical_notif_log_v1";

function readLog() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeLog(log) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(log));
  } catch {
    /* quota exceeded — non-fatal */
  }
}

function logKey(date, event) {
  // Gregorian weekday + biblical date combined so the same biblical day
  // can re-fire across consecutive Gregorian weeks if needed.
  return `${date.year}-${date.month}-${date.day}-${date.gregorianWeekday ?? "X"}-${event}`;
}

function alreadyFired(date, event) {
  return Boolean(readLog()[logKey(date, event)]);
}

function markFired(date, event) {
  const log = readLog();
  log[logKey(date, event)] = Date.now();
  const cutoff = Date.now() - 90 * 24 * 3600 * 1000;
  for (const k of Object.keys(log)) {
    if (log[k] < cutoff) delete log[k];
  }
  writeLog(log);
}

export default function LiturgicalNotifications({ user }) {
  const { celestialData } = useCelestial();
  const prevSnapshotRef = useRef(null);

  useEffect(() => {
    if (!user || !celestialData) return;

    const biblical = celestialData.biblical || {};
    const date = biblical.date || {};
    const sabbath = biblical.sabbath || {};

    const snapshot = {
      year: date.year,
      month: date.month,
      day: date.day,
      monthName: date.month_name,
      isAfterSunset: Boolean(date.is_after_sunset),
      isSabbath: Boolean(sabbath.is_sabbath),
      isPreparation: Boolean(sabbath.is_preparation),
      gregorianWeekday: sabbath.gregorian_weekday,
      gregorianWeekdayName: sabbath.gregorian_weekday_name,
    };

    if (!snapshot.day || !snapshot.monthName) {
      prevSnapshotRef.current = snapshot;
      return;
    }

    const prev = prevSnapshotRef.current;

    // 1. SUNDOWN crossover (biblical-day rollover at sundown)
    if (prev) {
      const dayChanged = prev.day !== snapshot.day || prev.month !== snapshot.month;
      const sundownCrossed = !prev.isAfterSunset && snapshot.isAfterSunset;
      if ((sundownCrossed || dayChanged) && !alreadyFired(snapshot, "sundown")) {
        toast(`🌒 Sundown — Day ${snapshot.day} of ${snapshot.monthName} has begun`, {
          description: "The biblical day transitions at sundown. A new day is here.",
          duration: 10000,
        });
        markFired(snapshot, "sundown");
      }
    }

    // 2. PREPARATION DAY (Gregorian Friday before sundown)
    if (snapshot.isPreparation && !alreadyFired(snapshot, "preparation")) {
      const isNewDayForUs = !prev || prev.day !== snapshot.day;
      if (isNewDayForUs) {
        toast(`🕯️ Preparation Day — ${snapshot.gregorianWeekdayName || "Friday"}`, {
          description:
            "Tonight at sundown the Sabbath begins. Use today to prepare meals, finish work, and ready your household.",
          duration: 12000,
        });
        markFired(snapshot, "preparation");
      }
    }

    // 3. SABBATH DAY (Friday sundown → Saturday sundown)
    if (snapshot.isSabbath && !alreadyFired(snapshot, "sabbath")) {
      const isNewDayForUs = !prev || prev.day !== snapshot.day || !prev.isSabbath;
      if (isNewDayForUs) {
        toast(`🕊️ Sabbath — Friday sundown → Saturday sundown`, {
          description:
            "The Sabbath has begun. Cease from work and rest. The Gate is OPEN — set apart for worship and study.",
          duration: 14000,
        });
        markFired(snapshot, "sabbath");
      }
    }

    prevSnapshotRef.current = snapshot;
  }, [user, celestialData]);

  return null;
}
